import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { Select } from '~/eds/src/components/Select/Select';
import { InputWidth } from '~/eds/src/types/InputWidth';
import { Option } from '~/eds/src/types/Option';
import { Nullable } from '~/types/Nullable';

import { DataFieldType } from '../../../enums/DataFieldType';
import { getIconFromFieldType } from '../utils';

interface Props {
  value: Nullable<DataFieldType>;
  onChange: (updatedValue: Nullable<DataFieldType>) => void;
  config?: {
    acceptFieldTypes?: DataFieldType[];
    shouldMergeFieldTypes?: boolean;
  };
  placeholder?: string;
  width?: InputWidth;
}

export const FieldTypeSelect = ({
  config = {},
  placeholder: overridePlaceholder,
  value,
  width,
  onChange,
}: Props) => {
  const intl = useIntl();
  const {
    acceptFieldTypes = Object.values(DataFieldType),
    shouldMergeFieldTypes = true,
  } = config;

  const options = useMemo(() => {
    const options: Option<DataFieldType>[] = [];
    const fieldTypeSet = new Set(acceptFieldTypes);
    // LEGACY feature: if either ARRAY_SINGLE or ARRAY_MULTIPLE is present, merge and only resolve to ARRAY_MULTIPLE
    if (shouldMergeFieldTypes) {
      if (
        fieldTypeSet.has(
          DataFieldType.ARRAY_SINGLE ||
            fieldTypeSet.has(DataFieldType.ARRAY_MULTIPLE),
        )
      ) {
        fieldTypeSet.add(DataFieldType.ARRAY_SINGLE);
        fieldTypeSet.add(DataFieldType.ARRAY_MULTIPLE);
        fieldTypeSet.delete(DataFieldType.ARRAY_SINGLE);
      }
    }
    const fieldTypeLabelsMap = getFieldTypeLabels(intl);
    fieldTypeSet.forEach((fieldType) => {
      const label =
        shouldMergeFieldTypes &&
        [DataFieldType.ARRAY_SINGLE, DataFieldType.ARRAY_MULTIPLE].includes(
          fieldType,
        )
          ? intl.formatMessage({
              id: 'FieldTypeSelect.dropdown',
              defaultMessage: 'Dropdown',
              description: 'Label for merged dropdown field types',
            })
          : fieldTypeLabelsMap[fieldType];
      options.push({
        leftIcon: getIconFromFieldType(fieldType),
        label,
        value: fieldType,
      });
    });
    return options;
  }, [acceptFieldTypes, intl, shouldMergeFieldTypes]);

  const placeholder =
    overridePlaceholder ??
    intl.formatMessage({
      id: 'FieldTypeSelect.placeholder',
      defaultMessage: 'Select a field type',
      description: 'Placeholder text for field type selection',
    });

  return (
    <Select
      enableSorting
      isClearable={false}
      isMulti={false}
      isSearchable={false}
      name="field-type-select"
      options={options}
      placeholder={placeholder}
      value={value}
      width={width}
      onChange={onChange}
    />
  );
};

const getFieldTypeLabels = (
  intl: IntlShape,
): Record<DataFieldType, string> => ({
  [DataFieldType.AGE]: intl.formatMessage({
    id: 'FieldTypeSelect.number',
    defaultMessage: 'Number',
    description: 'Label for age/number field type',
  }),
  [DataFieldType.ARRAY_MULTIPLE]: intl.formatMessage({
    id: 'FieldTypeSelect.dropdownMultiple',
    defaultMessage: 'Dropdown (Multiple)',
    description: 'Label for dropdown field type with multiple selection',
  }),
  [DataFieldType.ARRAY_SINGLE]: intl.formatMessage({
    id: 'FieldTypeSelect.dropdownSingle',
    defaultMessage: 'Dropdown (Single)',
    description: 'Label for dropdown field type with single selection',
  }),
  [DataFieldType.ATTACHMENT]: intl.formatMessage({
    id: 'FieldTypeSelect.attachment',
    defaultMessage: 'Attachment',
    description: 'Label for attachment field type',
  }),
  [DataFieldType.BOOLEAN]: intl.formatMessage({
    id: 'FieldTypeSelect.boolean',
    defaultMessage: 'Boolean',
    description: 'Label for boolean field type',
  }),
  [DataFieldType.CLAUSE]: intl.formatMessage({
    id: 'FieldTypeSelect.clause',
    defaultMessage: 'Clause',
    description: 'Label for clause field type',
  }),
  [DataFieldType.DATE]: intl.formatMessage({
    id: 'FieldTypeSelect.date',
    defaultMessage: 'Date',
    description: 'Label for date field type',
  }),
  [DataFieldType.NUMBER]: intl.formatMessage({
    id: 'FieldTypeSelect.numberField',
    defaultMessage: 'Number',
    description: 'Label for number field type',
  }),
  [DataFieldType.REFERENCE]: intl.formatMessage({
    id: 'FieldTypeSelect.reference',
    defaultMessage: 'Reference',
    description: 'Label for reference field type',
  }),
  [DataFieldType.STRING]: intl.formatMessage({
    id: 'FieldTypeSelect.text',
    defaultMessage: 'Text',
    description: 'Label for text field type',
  }),
  [DataFieldType.TEXT_AREA]: intl.formatMessage({
    id: 'FieldTypeSelect.textArea',
    defaultMessage: 'Text Area',
    description: 'Label for text area field type',
  }),
});
